// FTEDN Team - Beta Programs Management
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import './BetaMgmt.css';

interface BetaApplication {
  id: string;
  creatorName?: string;
  creatorEmail?: string;
  name?: string;
  email?: string;
  programName?: string;
  productName?: string;
  status?: string;
  appliedAt?: string;
}

function getItems<T>(response: unknown): T[] {
  const payload = response as { data?: T[] };
  return Array.isArray(payload?.data) ? payload.data : [];
}

function formatDate(value?: string): string {
  if (!value) return 'unknown';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

export function BetaMgmtPage() {
  const { get, patch } = useApi();
  const [applications, setApplications] = useState<BetaApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actingId, setActingId] = useState<string | null>(null);

  const loadApplications = useCallback(async () => {
    setError('');
    try {
      setApplications(getItems<BetaApplication>(await get('/beta/applications')));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load beta applications');
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => {
    void loadApplications();
  }, [loadApplications]);

  const pendingCount = useMemo(
    () => applications.filter((application) => (application.status ?? 'pending') === 'pending').length,
    [applications],
  );

  async function acceptApplication(application: BetaApplication) {
    setActingId(application.id);
    setError('');
    try {
      await patch(`/beta/applications/${application.id}/accept`);
      await loadApplications();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept beta application');
    } finally {
      setActingId(null);
    }
  }

  return (
    <div className="mgmt-page">
      <div className="mgmt-header">
        <h1>Beta Programs</h1>
        <p className="mgmt-subtitle">Review tester applications and cohorts</p>
        <div className="mgmt-stats">
          <span className="stat-chip">{applications.length} Applications</span>
          <span className="stat-chip approved">{pendingCount} Pending</span>
        </div>
      </div>
      {error && <div className="mgmt-error" role="alert">{error}</div>}
      <div className="mgmt-table">
        {loading && <div className="mgmt-empty">Loading beta applications...</div>}
        {!loading && applications.length === 0 && <div className="mgmt-empty">No beta applications yet.</div>}
        {applications.map((application) => {
          const creatorName = application.creatorName || application.name || application.email || application.creatorEmail || 'Unknown creator';
          const programName = application.programName || application.productName || 'Beta program';
          const status = application.status || 'pending';
          return (
            <div key={application.id} className="mgmt-row">
              <div className="mgmt-row-info">
                <strong>{creatorName}</strong>
                <span>{programName}</span>
                {(application.creatorEmail || application.email) && <span>{application.creatorEmail || application.email}</span>}
              </div>
              <span className="mgmt-time">{formatDate(application.appliedAt)}</span>
              <span className={`status-badge ${status}`}>{status}</span>
              {status === 'pending' && (
                <div className="mgmt-actions">
                  <button className="action-btn approve" disabled={actingId === application.id} onClick={() => void acceptApplication(application)} aria-label={`Accept ${creatorName}`}>
                    Accept
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
